// src/components/SecretCodeModal.js
import React, { useEffect, useMemo, useState } from "react";
import {
Modal,
Pressable,
Text,
StyleSheet,
TextInput,
TouchableOpacity,
View,
Platform,
} from "react-native";
import { useTranslation } from "../i18n";

export default function SecretCodeModal({
visible,
onClose,
onConfirm,
expectedCode = null, // ✅ frontend-only MVP
}) {
const { t } = useTranslation();

const [code, setCode] = useState("");
const [error, setError] = useState("");

const trimmed = useMemo(() => code.trim(), [code]);

useEffect(() => {
if (!visible) {
setCode("");
setError("");
}
}, [visible]);

const handleClose = () => {
setCode("");
setError("");
onClose?.();
};

const handleConfirm = () => {
// ✅ no error when empty, just do nothing
if (!trimmed) return;

// ✅ validate against expectedCode (if provided)
if (expectedCode !== null && trimmed !== String(expectedCode)) {
setError(t("modal.wrongSecretCode"));
return;
}

setError("");
onConfirm?.(trimmed);
};

return (
<Modal
visible={visible}
transparent
animationType="fade"
onRequestClose={handleClose}
>
<Pressable style={S.backdrop} onPress={handleClose}>
<Pressable style={S.sheet} onPress={() => {}}>
<Text style={S.title}>{t("modal.enterSecretCode")}</Text>

<TextInput
value={code}
onChangeText={(v) => {
setCode(v);
if (error) setError("");
}}
placeholder={t("modal.placeholder")}
placeholderTextColor="rgba(17,24,39,0.35)"
style={S.input}
autoFocus
keyboardType={Platform.OS === "ios" ? "number-pad" : "numeric"}
returnKeyType="done"
secureTextEntry
maxLength={12}
onSubmitEditing={handleConfirm}
/>

{!!error && <Text style={S.errorText}>{error}</Text>}

<View style={S.actions}>
<TouchableOpacity
style={[S.btn, S.btnGhost]}
onPress={handleClose}
activeOpacity={0.9}
>
<Text style={[S.btnText, S.btnTextGhost]}>
{t("common.cancel")}
</Text>
</TouchableOpacity>

<TouchableOpacity
style={[S.btn, S.btnPrimary, !trimmed && { opacity: 0.5 }]}
onPress={handleConfirm}
activeOpacity={0.9}
disabled={!trimmed}
>
<Text style={[S.btnText, S.btnTextPrimary]}>
{t("common.confirm")}
</Text>
</TouchableOpacity>
</View>
</Pressable>
</Pressable>
</Modal>
);
}

const DARK_GREEN = "#0F3C2F";

const S = StyleSheet.create({
backdrop: {
flex: 1,
backgroundColor: "rgba(0,0,0,0.45)",
justifyContent: "center",
alignItems: "center",
padding: 18,
},
sheet: {
width: "100%",
maxWidth: 420,
backgroundColor: "#FFFFFF",
borderRadius: 18,
padding: 18,
shadowColor: "#000",
shadowOpacity: 0.18,
shadowRadius: 18,
shadowOffset: { width: 0, height: 14 },
elevation: 10,
},
title: {
fontSize: 18,
fontWeight: "800",
color: "#111827",
marginBottom: 12,
textAlign: "center",
},
input: {
height: 46,
borderRadius: 14,
borderWidth: 1,
borderColor: "rgba(0,0,0,0.10)",
backgroundColor: "rgba(0,0,0,0.04)",
paddingHorizontal: 14,
fontSize: 16,
fontWeight: "700",
color: "#111827",
textAlign: "center",
letterSpacing: 2,
marginBottom: 10,
},
errorText: {
marginBottom: 10,
textAlign: "center",
color: "#DC2626",
fontWeight: "800",
fontSize: 13,
},
actions: {
flexDirection: "row",
gap: 10,
},
btn: {
flex: 1,
height: 42,
borderRadius: 14,
alignItems: "center",
justifyContent: "center",
},
btnGhost: {
backgroundColor: "rgba(0,0,0,0.06)",
},
btnPrimary: {
backgroundColor: DARK_GREEN,
},
btnText: {
fontSize: 14,
fontWeight: "800",
letterSpacing: 0.2,
},
btnTextGhost: {
color: "#111827",
},
btnTextPrimary: {
color: "#FFFFFF",
},
});